<script>
import {Link} from "@inertiajs/vue3";
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";

export default {
    name: "HomeVideoBlock",
    components: {IconAngleLeft, Link},
    data() {
        return {
            showCta: false,
            videoDuration: 0,
            lastTime: 0,
            ctaThresholdSeconds: 5, // за сколько секунд до конца показывать CTA
        };
    },
    methods: {
        onLoaded(e) {
            this.videoDuration = e.target.duration || 0;
        },
        onTimeUpdate(e) {
            const t = e.target.currentTime || 0;
            const dur = this.videoDuration || e.target.duration || 0;

            if (dur > 0) {
                const remaining = dur - t;
                this.showCta = remaining <= this.ctaThresholdSeconds;
            }

            // Если видео зациклилось или перемотали назад — прячем CTA
            if (t < this.lastTime - 0.3) {
                this.showCta = false;
            }
            this.lastTime = t;
        },
        onPlay() {
            // при старте воспроизведения прячем CTA (на случай паузы в конце)
            this.showCta = false;
        },
        onEnded() {
            // с loop событие вызывается перед рестартом — тоже прячем CTA
            this.showCta = false;
        },
    },
};
</script>

<template>
    <div class="w-full bg-grayBg rounded-3xl text-black overflow-hidden">
        <div class="grid grid-cols-1 md:grid-cols-2 h-full md:min-h-[325px]">
            <!-- Видео + H1 + CTA -->
            <Link
                :href="route('aboutCompany')"
                class="group block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffcf33] focus-visible:ring-offset-2 focus-visible:ring-offset-grayBg rounded-3xl"
                aria-label="Подробнее о компании «Дорожные Плиты»"
            >
                <div class="group relative w-full h-[250px] md:h-full min-h-[220px] rounded-3xl overflow-hidden">
                    <!-- Видео -->
                    <video
                        class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 will-change-transform"
                        autoplay
                        muted
                        loop
                        playsinline
                        preload="metadata"
                        poster="@videos/poster.jpg"
                        controlslist="nodownload noplaybackrate"
                        disablepictureinpicture
                        @loadedmetadata="onLoaded"
                        @timeupdate="onTimeUpdate"
                        @play="onPlay"
                        @ended="onEnded"
                    >
                        <source src="@videos/video.webm" type="video/webm"/>
                        <source src="@videos/video.mp4" type="video/mp4"/>
                        Ваш браузер не поддерживает воспроизведение видео.
                    </video>

                    <!-- Затемнение включается только когда showCta = true -->
                    <div
                        :class="[
              'absolute inset-0 bg-black/80 transition-opacity duration-300 md:group-hover:opacity-80',
              showCta ? 'opacity-80' : 'opacity-0'
            ]"
                    ></div>

                    <!-- Заголовок: скрываем, когда показываем CTA -->
                    <h1
                        :class="[
              'md:group-hover:opacity-0 flex flex-col gap-2 glass p-5 absolute left-0 m-5 max-w-[450px] text-2xl xl:text-3xl font-bold text-white transition-opacity duration-300',
              showCta ? 'opacity-0' : 'opacity-100'
            ]"
                    >
                        <span>ООО «Дорожные Плиты»</span>
                        <span class="font-medium text-base leading-tight">
                            Железобетонные изделия от поставщика.
                        </span>
                    </h1>

                    <!-- Центрированная CTA: появляется ближе к концу -->
                    <div class="absolute inset-0 flex items-center justify-center">
                        <div
                            :class="[
               'md:group-hover:opacity-100 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium ring-1 ring-white/30 backdrop-blur-md shadow-lg transition duration-300 ease-out',
               showCta ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
             ]">
                            Узнать больше
                            <IconAngleLeft class="w-4 h-4 rotate-180"/>
                        </div>
                    </div>

                    <!-- Невидимое описание для доступности -->
                    <p class="sr-only">
                        Промо-видео «Дорожные Плиты»: производство, склад и доставка железобетонных изделий.
                    </p>
                </div>
            </Link>

            <!-- Текст -->
            <div class="flex-1 flex flex-col p-5 md:p-10">
                <div class="flex flex-col gap-2 text-sm">
                    <p>
                        ООО «Дорожные Плиты» — современный завод по производству железобетонных изделий (ЖБИ) и бетонных
                        смесей для строительства дорог, площадок, промышленных объектов и благоустройства территорий.
                    </p>
                    <p>
                        На предприятии используется полный производственный цикл: от подготовки сырья до выпуска готовых
                        плит и изделий. Для приготовления бетона применяются только сертифицированные материалы — цемент
                        высоких марок, щебень правильной фракции, очищенный песок и современные химические добавки,
                        которые повышают подвижность смеси, морозостойкость и долговечность изделий.
                    </p>
                    <p>
                        Благодаря современным технологиям и строгому контролю качества продукция ООО «Дорожные Плиты»
                        отличается высокой надёжностью, устойчивостью к тяжёлым нагрузкам и долговечностью эксплуатации
                        даже в суровых климатических условиях.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.glass {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 16px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

@media (prefers-reduced-motion: reduce) {
    video {
        animation: none !important;
    }
}
</style>
